import { useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button, Chip } from '@/components/ui'
import { useRefreshDroppedRows, useResolveDroppedRow } from '../api/queries'
import type { DroppedRow } from '../api/types'
import { money } from '../utils/price-sheet-review'

export interface DroppedTiersCardProps {
  intakeId: string
  vendorName: string
  rows: DroppedRow[]
  /** YYYY-MM-DD the sheet takes effect; an ended row closes the day before. */
  effectiveDate: string | null
}

function dayBefore(iso: string | null): string {
  const d = iso ? new Date(`${iso}T00:00:00`) : new Date()
  d.setDate(d.getDate() - 1)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function qty(value: number | null, uom: string | null): string {
  if (value == null) return '—'
  return `${value.toLocaleString()}${uom ? ` ${uom}` : ''}`
}

/**
 * Alert for prices Odoo still has that the new sheet no longer lists — a pack
 * or MOQ tier the vendor stopped quoting (Givaudan dropped Schizandra LG's
 * 1 kg tier; Odoo kept quoting it at $36.42). Each row can be ended in Odoo
 * (its validity closes the day before the sheet takes effect) or kept.
 */
export function DroppedTiersCard({
  intakeId,
  vendorName,
  rows,
  effectiveDate,
}: DroppedTiersCardProps) {
  const resolve = useResolveDroppedRow()
  const refresh = useRefreshDroppedRows()
  const [showHandled, setShowHandled] = useState(false)

  const open = rows.filter((r) => r.action == null)
  const handled = rows.filter((r) => r.action != null)
  if (rows.length === 0) return null

  function act(row: DroppedRow, action: 'end' | 'keep' | 'reopen') {
    resolve.mutate(
      { intakeId, rowId: row.id, action },
      {
        onSuccess: () =>
          toast.success(
            action === 'end'
              ? `${row.product_name ?? 'Row'} ${qty(row.min_qty, row.uom)} ends ${dayBefore(effectiveDate)} in Odoo.`
              : action === 'keep'
                ? 'Kept as is.'
                : 'Back on the list.',
          ),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not update the row.'),
      },
    )
  }

  const shown = showHandled ? rows : open

  return (
    <section
      aria-label="Prices no longer on the sheet"
      className={`rounded-xs border px-3 py-2 text-sm ${
        open.length > 0
          ? 'border-warning-border bg-warning-bg'
          : 'border-border-default bg-surface-sunk'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {open.length > 0 ? <AlertTriangle size={16} className="text-warning-text" /> : null}
        <span className={open.length > 0 ? 'font-medium text-warning-text' : 'text-secondary'}>
          {open.length > 0
            ? `${open.length} Odoo price${open.length === 1 ? '' : 's'} ${open.length === 1 ? 'is' : 'are'} no longer on ${vendorName}'s sheet`
            : `Every price ${vendorName}'s sheet dropped has been handled`}
        </span>
        <span className="text-xs text-secondary">
          {open.length > 0
            ? `A pack or quantity tier the vendor stopped quoting stays live in Odoo until you end it. Ending sets its last valid day to ${dayBefore(effectiveDate)}.`
            : null}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {handled.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setShowHandled((v) => !v)}>
              {showHandled ? 'Hide handled' : `Show handled (${handled.length})`}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            disabled={refresh.isPending}
            onClick={() =>
              refresh.mutate(
                { intakeId },
                {
                  onError: (err) =>
                    toast.error(err instanceof Error ? err.message : 'Recheck failed.'),
                },
              )
            }
          >
            <RefreshCw size={12} /> Recheck
          </Button>
        </span>
      </div>

      {shown.length > 0 ? (
        <table className="mt-2 w-full text-sm">
          <thead className="text-role-table-header">
            <tr>
              <th className="px-2 py-1 text-left">SPI product</th>
              <th className="px-2 py-1 text-left">Vendor code</th>
              <th className="px-2 py-1 text-right">Min qty</th>
              <th className="px-2 py-1 text-right">Pack</th>
              <th className="px-2 py-1 text-right">Odoo price</th>
              <th className="px-2 py-1 text-left">Valid</th>
              <th className="px-2 py-1 text-left">Why</th>
              <th className="px-2 py-1" />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-t border-border-subtle">
                <td className="px-2 py-1 text-primary">
                  {r.product_name ?? r.vendor_product_name ?? '—'}
                </td>
                <td className="px-2 py-1 text-secondary">{r.vendor_product_code ?? '—'}</td>
                <td className="px-2 py-1 text-right">{qty(r.min_qty, r.uom)}</td>
                <td className="px-2 py-1 text-right text-tertiary">
                  {r.pack_size ? qty(r.pack_size, r.uom) : '—'}
                </td>
                <td className="px-2 py-1 text-right">
                  {money(r.price)}
                  {r.uom ? `/${r.uom}` : ''}
                </td>
                <td className="px-2 py-1 text-xs text-tertiary">
                  {r.date_start ?? '…'} – {r.date_end ?? 'open'}
                </td>
                <td className="px-2 py-1 text-xs text-secondary">{r.reason}</td>
                <td className="px-2 py-1 text-right">
                  {r.action === 'end' ? (
                    <Chip size="xs" variant="success">
                      Ended {r.date_end}
                    </Chip>
                  ) : r.action === 'keep' ? (
                    <span className="inline-flex items-center gap-2">
                      <Chip size="xs" variant="neutral">
                        Kept
                      </Chip>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={resolve.isPending}
                        onClick={() => act(r, 'reopen')}
                      >
                        Undo
                      </Button>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={resolve.isPending}
                        onClick={() => act(r, 'end')}
                      >
                        End this price
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={resolve.isPending}
                        onClick={() => act(r, 'keep')}
                      >
                        Keep
                      </Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  )
}
